/**
 * Geometry for a third-level submenu that has been lifted out of its scrolling
 * parent (see SubmenuFlyoutDirective).
 *
 * The second-level dropdown has to cap its height and scroll - Admin carries 25
 * children, taller than a laptop viewport - and a scroll container clips its
 * absolutely positioned descendants on BOTH axes (once `overflow-y` is not
 * `visible`, `overflow-x: visible` computes to `auto`). The third-level panels
 * open at `left: 100%`, so they would be clipped at the dropdown's right edge.
 * They are therefore positioned `fixed` against the viewport instead, which is
 * what this function computes.
 */

export interface Rect {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface FlyoutPosition {
  top: number;
  left: number;
  /** True when the panel had to open to the left of its anchor to stay on screen. */
  flipped: boolean;
}

/** Breathing room kept between the panel and the viewport edge. */
export const FLYOUT_VIEWPORT_MARGIN = 8;

/** Overlaps the anchor's 1px border so the panel still reads as attached to it. */
const BORDER_OVERLAP = 1;

export const computeFlyoutPosition = (
  anchor: Rect,
  flyout: Size,
  viewport: Viewport,
): FlyoutPosition => {
  const margin = FLYOUT_VIEWPORT_MARGIN;

  // Preferred side is to the right of the anchor, matching the `left: 100%` the
  // stylesheet uses when there is no scroll container in play.
  let left = anchor.right - BORDER_OVERLAP;
  let flipped = false;

  if (left + flyout.width > viewport.width - margin) {
    const flippedLeft = anchor.left - flyout.width + BORDER_OVERLAP;
    // Only flip when the left side actually has more room - on a narrow viewport
    // both sides can overflow, and clamping right is the less broken of the two.
    if (flippedLeft >= margin) {
      left = flippedLeft;
      flipped = true;
    } else {
      left = Math.max(margin, viewport.width - margin - flyout.width);
    }
  }

  // Top-aligned with the anchor, then pulled up far enough to keep the whole
  // panel on screen. Never pushed above the margin, so a panel taller than the
  // viewport starts at the top and relies on its own scrolling.
  let top = anchor.top;
  if (top + flyout.height > viewport.height - margin) {
    top = viewport.height - margin - flyout.height;
  }
  top = Math.max(margin, top);

  return { top, left, flipped };
};
